import React, {Component, createRef, FunctionComponent, Ref} from "react";
import {DateTime} from "luxon";
import { isSearchComplete } from "../../../hooks/useSearchCompletion";
import {
    Button,
    Grid,
    Input,
    Modal,
    Tooltip,
    Form,
    notification,
    Pagination,
    InputRef,
    Spin
} from "antd";

import {ColumnType, Key, SorterResult, TableCurrentDataSource, TablePaginationConfig} from "antd/es/table/interface";
import {
    AlignLeftOutlined,
    CloseCircleOutlined,
    DownloadOutlined,
    FullscreenExitOutlined,
    FullscreenOutlined,
    ReloadOutlined,
    UpOutlined,
    LoadingOutlined,
    FilterOutlined
} from "@ant-design/icons";

import {Helmet} from "react-helmet";
import Title from "antd/es/typography/Title";
import LoadingStar from "../loaders/LoadingStar";
import FilterStorage from "../../../common/utils/FilterStorage";
import LocalStorage from "../../../common/utils/LocalStorage";
import {UserContext} from "context";
import {ChangeEventShim} from "shared/components/Components";
import {ExpandableConfig} from "rc-table/lib/interface";
import FiltersBar, {Filter} from "./filters-bar/FiltersBar";

import {Error as ErrorWarning} from "../errors/Error";

import "./Table.abstract.scss"
import SessionStorage from "../../../common/utils/SessionStorage";

import BaseTable from "./BaseTable";
import EmptyOverlay from "./EmptyOverlay";
import { DateFilterObj } from "../../filters/date-filter/DateFilter";
import { ManageColumns } from "./manage-columns/ManageColumns";

import { DecodedQueryFilter, TableFilterQueryHelper } from "./TableFilterQueryHelper";

export interface TableProps {
    id?: string;
    sorter?: boolean;
    dataIndex?: string;
    offsetHeader?: number;
    context: UserContext;
    useLegacyEndpoints: boolean;
    breaks: typeof Grid.useBreakpoint;
    displayHideFilters?: boolean;
    hideDefaultFilters?: boolean;
    noSelect?:boolean;
    hasSidebar?:boolean;
    CellRender?:any;
    legacyTable?:boolean
    filtersRef?:Ref<any>;
    rowHeight?:number;
    useEstimatedRowHeight?: boolean;
    extra?:Record<any, any>;
    /** Extra class names per row (e.g. deleted / inactive / group boundaries); appended to whatever the table sets. */
    rowClassName?: (record: any, index: number) => string;
}

export type TableFilters = Record<string, any>;

export interface SavedFilter {
    _id: string;
    creator: string;
    sharedWith?: string[];
    filterData: any;
    name: string;
    key: string;
}

export interface TableState<R>{
    breaks: typeof Grid.useBreakpoint;
    initialised: boolean;
    loading: boolean;
    rowData: R[];
    filters: TableFilters;
    showFilters: boolean;
    selectedRows: R[];
    currentPage: number;
    itemsPerPage: number;
    disabledFilters: Set<keyof TableFilters>;
    sortField?: string;
    sortOrder: "ascend" | "descend";
    error?: Error;
    tempColumnOrder: string[];
    columnOrder: string[];
    hiddenColumns: string[];
    pinnedColumns: string[];
    columnsWidth: ColsWidth[];
    showManageColumnsModal: boolean;
    disabledColumns: string[];
    showExportModal: boolean;
    exportFilename: string;
    users: { email: string; name: string; _id: string }[];
    workflows: { title: string; description: string; _id: string }[];
    savedFilters: SavedFilter[];
    currentSavedFilter?: SavedFilter;
    pinnedLeft?: string;
    pinnedRight?: string;
    meta: any;
    filtersLoading: boolean;
    changedIds?: Set<string>;
    reqId?:string;
    streamingComplete?: boolean;
    pendingFilters: TableFilters;
    hasPendingFilters: boolean;
    canSaveFilters: boolean;
}

export interface FilterComponent {
    id: string;
    name: string;
    title: string;
    component: FunctionComponent<any>;
    left?: boolean;
    active?: boolean;
}

export interface BaseRow extends Record<string, any> {
    key?: string | number;
}

export interface IPagination {
    currentPage: number;
    totalPages: number;
    totalResults?: number;
}

export interface FetchResponseData<R> {
    data: R[];
    pagination: IPagination;
}

export interface FetchResponseDataFlat<R> {
    data: R[];
}

export interface Column<R> extends Omit<ColumnType<R>, "fixed"> {
    id: string;
    title: string;
    fixed?: string | boolean;
    frozen?: string | boolean;
}

interface abstractColumn<R> extends Column<R> {
    FilterComponent: any
}

interface ColsWidth {
    key: string;
    width: number;
}


import ExportJsonExcel from "common/utils/exportExcel";

const {Item} = Form;

export default abstract class TableBaseAbstract<R extends BaseRow> extends Component<TableProps, TableState<R>> {

    abstract title: string;
    abstract pageTitle: string;
    abstract FilterComponents: FilterComponent[];
    abstract columns: Column<R>[];
    abstract getData():Promise<void>;
    abstract defaultSortField: string;
    inputRef = React.createRef<InputRef>();

    column(props: abstractColumn<R>) {

        const
            {FilterComponent, ...config} = props,
            { sortField, sortOrder } = this.state;

        return {
            filterDropdown: FilterComponent
                // @ts-ignore
                ? <FilterComponent
                    id={config.id}
                    key={config.dataIndex}
                    name={config.dataIndex}
                    //@ts-ignore
                    value={this.state.filters[config.dataIndex]}
                    onRemove={this.onRemoveFromList}
                    onChange={this.onUpdateFilter}
                    disabled={false}
                    onClearFilter={this.onClearFilter}
                    forceOpen={true}
                /> : undefined,
            ...config,
            ...(config && config.sorter === true ? {
                sortOrder: sortField === config.dataIndex ? sortOrder : undefined
            } : {})
        }
    }


    public pageClass = "defaultTable";
    private initialMoreLessOptions: string[] = [];
    protected userContext: UserContext;
    private filterStore!: FilterStorage;
    private tableSettingsStore!: LocalStorage;
    private cacheStorage?: SessionStorage;
    protected bustCache = () => {
        this.cacheStorage && this.cacheStorage.clear();
    }

    public mapping?: Record<keyof R, string>;

    public columnMapping(): Column<R>[] {
        let 
            cols = this.columns.filter((col) => this.state.disabledColumns.indexOf(col.id) === -1
        );

        cols = cols.sort((a, b) =>
                this.state.columnOrder.indexOf(a.id) -
                this.state.columnOrder.indexOf(b.id)
        );

        return cols.map((col) => {
            const existentColumn = this.state.columnsWidth.find((c)=> c.key === col.dataIndex);
            return { ...col, width: existentColumn ? existentColumn.width : col.width }
        });
    }


    protected constructor(props: TableProps) {

        super(props);

        this.userContext = props.context;

        this.state = {
            columnOrder: [],
            hiddenColumns: [],
            pinnedColumns: [],
            breaks: props.breaks,
            initialised: false,
            loading: false,
            rowData: [],
            filters: {},
            showFilters: true,
            selectedRows: [],
            currentPage: 1,
            itemsPerPage: 100,
            disabledFilters: new Set(),
            sortOrder: "descend",
            showManageColumnsModal: false,
            tempColumnOrder: [],
            disabledColumns: [],
            columnsWidth: [],
            showExportModal: false,
            exportFilename: "",
            users: [],
            workflows: [],
            savedFilters: [],
            currentSavedFilter: undefined,
            pinnedLeft: "",
            pinnedRight: "",
            meta: {},
            filtersLoading: true,
            streamingComplete: true,
            pendingFilters: {},
            hasPendingFilters: false,
            canSaveFilters: false
        }
    }

    get breaks() {
        return this.state.breaks;
    }

    protected clear = () => {
        this.setFilterParams();
        this.filterStore.clear();
        this.filterStore.add({});
        this.filterStore.save();
        const data = this.tableSettingsStore.data || {};
        data.currentPage = 1;
        data.sortOrder = undefined;
        data.sortField = undefined;
        data.currentSavedFilter = undefined;
        this.tableSettingsStore.data = data;
        this.tableSettingsStore.data.controlsEnabled = false;
        this.tableSettingsStore.data.showFilters = true;
        this.bustCache();
        // old version to see all filters on clear all
        // const newFilterComponents = this.FilterComponents.map((f)=> {
        //     f.active = true;
        //     return f;
        // })

        // this.FilterComponents = newFilterComponents;

        this.setState({
            filters: {},
            currentPage: 1,
            selectedRows: [],
            error: undefined,
            sortField: this.defaultSortField,
            sortOrder: "descend",
            currentSavedFilter: undefined,
            pendingFilters: {},
            hasPendingFilters: false
        }, this.getData);

    }

    protected updateFilters = (filters: TableFilters) => {

        const updated = this.disabledFilterRules(filters);
        this.filterStore.clear();
        this.filterStore.add(updated);
        this.filterStore.save();

        // Store in URL
        this.setFilterParams({filters});

        this.bustCache();

        this.setState({filters: updated, currentPage: 1}, this.getData);
    }

    /**
     * Reads any filter parameters from the Query string 
     * and returns them as a filter collection
     */
    private readFilterParams() : DecodedQueryFilter | null {

        // Get the params from the URL
        const params = new URLSearchParams(globalThis.location.search);

        // Get the filter type
        const filterType = params.get("ft");
        const filterValue = params.get("fv");

        // Decode and return the filters
        if (filterType && filterValue) {
            return TableFilterQueryHelper.decode({
                filterType: filterType,
                filterValue: filterValue
            })
        }

        return null;

    }

    /**
     * Encodes and sets the filters on the query string, removes 
     * any existing filter if no filter is specified
     * @param filters 
     */
    private setFilterParams(decodedQueryFilter?: DecodedQueryFilter) {

        // Get the current URL params
        const params = new URLSearchParams(globalThis.location.search);

        // Check there are filters to encode
        if (decodedQueryFilter && Object.keys(decodedQueryFilter.filters).length > 0) {
            const encodedQueryFilter = TableFilterQueryHelper.encode(decodedQueryFilter);

            // Set the filter
            params.set("ft", encodedQueryFilter.filterType); // Indicate using LZ compression, may support other types later
            params.set("fv", encodedQueryFilter.filterValue);
        } else {
            // Remove any existing filters
            params.delete("ft");
            params.delete("fv");
        }

        // Set the URL without causing a navigation
        const paramString = params.toString();
        let newUrl = `${globalThis.location.pathname}`;
        if (paramString) {
            newUrl = `${newUrl}?${paramString}`;
        }

        globalThis.history.replaceState({}, '', newUrl);

    }

    public disabledFilterRules(filters: TableFilters) {
        return filters;
    }

    private tempColumnData?: SessionStorage;


    set users(value: TableState<R>["users"]) {
        this.setState({users: value})
    }

    get users() {
        return this.state.users;
    }

    set workflows(value: TableState<R>["workflows"]) {
        this.setState({workflows: value})
    }

    get workflows() {
        return this.state.workflows;
    }

    protected abortController = new AbortController();

    public defaultDisabledColumns:string[] = [];

    /**
     * Initialises the local and remote storage for filters
     */
    private async intialiseFilterStorage() {
        this.filterStore = new FilterStorage(this.props.id || this.title);
        this.tableSettingsStore = new LocalStorage(this.props.id ? this.props.id.concat("-settings") : this.title.concat("-settings"));
        this.cacheStorage = new SessionStorage(this.props.id ? this.props.id.concat("-cache") : this.title.concat("-cache"));

        this.userContext.gpSocket.filterStorage.emit("getAll", this.props.id ? this.props.id : this.title, ( data:any ) => {
            this.setState({ savedFilters: data, filtersLoading: false } )
        })

        this.userContext.gpSocket.filterStorage.on( `updated-${this.props.id ? this.props.id : this.title}`, ( data:any ) => {
            this.setState({ savedFilters: data, filtersLoading: false } )
        } )
    }

    /**
     * Initialises the settings for the table
     */
    private async initialiseTableSettings() {
        const settings = this.tableSettingsStore.data || await this.settingsLoad();

        if (!settings.columnOrder) {
            settings.columnOrder = this.columns.map((column) => {
                return column.id
            })

            this.tableSettingsStore.set(settings);
        }
        else {
            // Columns added after the user's settings were persisted are
            // missing from columnOrder; indexOf(-1) would sort them first.
            // Slot them in at their default position instead.
            let merged = false;
            this.columns.forEach((column, index) => {
                if (settings.columnOrder.indexOf(column.id) === -1) {
                    settings.columnOrder.splice(Math.min(index, settings.columnOrder.length), 0, column.id);
                    merged = true;
                }
            });

            if (merged) {
                this.tableSettingsStore.set(settings);
            }
        }

        if (!settings.pinnedLeft) {
            settings.pinnedLeft = this.state.pinnedColumns.filter((item: any) => item.fixed === 'left').map((i: any) => i.id);
            this.tableSettingsStore.set(settings);
        }

        if (!settings.pinnedRight) {
            settings.pinnedRight = this.state.pinnedColumns.filter((item: any) => item.fixed === 'right').map((i: any) => i.id);
            this.tableSettingsStore.set(settings);
        }

        if (!settings.disabledColumns) {
            settings.disabledColumns = this.defaultDisabledColumns;
            this.tableSettingsStore.set(settings);
        }

        if (!settings.controlsEnabled) {
            settings.controlsEnabled = false;
            this.tableSettingsStore.set(settings);
        }

        if (!settings.columnsWidth) {
            settings.columnsWidth = this.columns.map((col) => {return {key: col.dataIndex, width: col.width}})
            this.tableSettingsStore.set(settings);
        }

        if (!settings.hasOwnProperty('showFilters')) {
            settings.showFilters = true;
            this.tableSettingsStore.set(settings);
        }

        // All filters are always active (More/Less feature removed)
        this.FilterComponents = this.FilterComponents.map((filter: FilterComponent) => {
            filter.active = true;
            return filter;
        })
    }

    /**
     * Determines which filter to use for the initial filter on loading the 
     * component, preference order is: 
     * 1. Filter from URL
     * 2. Filter from Saved Filter store
     * 3. Filter from local storage (filter user last used)
     * 4. No Filter
     */
    private getInitialFilter(currentSavedFilter: any) : TableFilters {

        // Check if there is a filter from the URL
        const decodedQueryFilter = this.readFilterParams();
        if (decodedQueryFilter) {
            if (decodedQueryFilter.filters) {
                return this.disabledFilterRules(decodedQueryFilter.filters);
            }
        }

        // Check if there is a Saved Filter
        if (this.filterStore.filters) {
            return this.disabledFilterRules(this.filterStore.filters);
        }

        // Check if there is a current filter
        if (currentSavedFilter) {
            return currentSavedFilter.filterData;
        }

        // Return no filter
        return {};
    }

    /**
     * Configure the component on load
     */
    private async intialiseComponent() {

        // Set the document title
        document.title = this.title;

        // Initialise local and remote filter storage
        await this.intialiseFilterStorage();

        // Initialise Table Settings
        await this.initialiseTableSettings();

        // Set the initial table state
        const {
            itemsPerPage,
            currentPage,
            sortField,
            sortOrder,
            columnOrder,
            pinnedLeft,
            pinnedRight,
            disabledColumns,
            columnsWidth,
            currentSavedFilter,
            showFilters
        } = this.tableSettingsStore.data || {
            itemsPerPage: 100,
            currentPage: 1
        };

        this.tempColumnData = new SessionStorage("tempColumnData");

        const colData = this.tempColumnData.data || {};

        this.tempColumnData.set(colData);

        const initialFilters = this.getInitialFilter(currentSavedFilter);

        this.setState({
            filters: initialFilters,
            itemsPerPage: itemsPerPage || 200,
            currentPage: currentPage || 1,
            sortOrder,
            sortField: sortField || this.defaultSortField,
            columnOrder,
            pinnedLeft,
            pinnedRight,
            disabledColumns,
            columnsWidth,
            currentSavedFilter,
            showFilters,
            pendingFilters: initialFilters,
            hasPendingFilters: false
        }, async () => {
            this.setState({initialised: true})
            await this.getData();
        })

        const body = document.querySelector("body");
        if ( body ) body.style.overflow = "hidden"

    }



    componentDidMount() {

        globalThis.addEventListener("mercury:session-restored", this.handleSessionRestored);
        this.intialiseComponent();
    }

    componentWillUnmount() {
        this.abortController.abort();
        globalThis.removeEventListener("mercury:session-restored", this.handleSessionRestored);
        const body = document.querySelector("body");
        if ( body ) body.style.removeProperty( "overflow" );
    }

    settingsFields = ["disabledColumns", "columnOrder", "pinnedLeft", "pinnedRight"];

    updateTimer: any;

    public async onSettingsStore() {
    }

    public async settingsLoad() {
        return {};
    }

    componentDidUpdate(prevProps: TableProps, prevState: TableState<R>) {

        if (JSON.stringify(this.state.breaks) !== JSON.stringify(this.props.breaks)) {
            this.setState({breaks: this.props.breaks});
        }

        if (!prevState.showExportModal && this.state.showExportModal) {
            setTimeout(() => {
                this.inputRef.current?.focus?.();
            }, 100);
        }

        const
            previous = {},
            current = {};
        this.settingsFields.forEach((field) => {
            //@ts-ignore
            previous[field] = prevState[field];
        })
        this.settingsFields.forEach((field) => {
            //@ts-ignore
            current[field] = this.state[field];
        });

        if (JSON.stringify(previous) !== JSON.stringify(current)) {
            this.tableSettingsStore.set({...this.tableSettingsStore.data, ...current})
            clearTimeout(this.updateTimer);
            this.updateTimer = setTimeout(() => this.onSettingsStore(), 2000);
        }
    }

    endpoint() {
        return "/projects"
    }

    public uri() {
        return this.endpoint()
            .concat("?")
            .concat(
                Object.keys(this.state.filters)
                    .map(key => key + '=' + this.state.filters[key]).join('&')
            );
    }

    public body() {
        return this.state.filters;
    }

    updateFilter(e: ChangeEventShim, filters: TableFilters) {

        const
            {target} = e;
        filters[target.name] = target.value;
        Object.keys(filters).map((key) => {
            if (!filters[key] || (Array.isArray(filters[key]) && filters[key].length === 0)) {
                delete filters[key];
            }
            return null;
        })
    }

    public filtersToQuery() {
        const
            query: Record<any, any> = Object.assign({}, this.filters);

        Object.keys(query).forEach(k => {
            query[k] = DateFilterObj.getDateRange(query[k])
        });

        return query;
    }

    get filters() {
        return this.state.filters;
    }

    getAppliedFilterCount = () => {
        return Object.keys(this.state.filters || {}).length;
    }

    isFilterApplied = (filterKey: string) => {
        const value = this.state.filters?.[filterKey];

        if (Array.isArray(value)) {
            return value.length > 0;
        }

        return value !== undefined &&
            value !== null &&
            value !== "";
    };

    // set filters(value) {
    //     this.bustCache();
    //     this.updateFilters(value);
    // }

    onUpdateFilter = (e: ChangeEventShim | ChangeEventShim[]) => {
        const filters = { ...this.state.pendingFilters };

        if (Array.isArray(e)) {
            e.forEach((item) => {
                this.updateFilter(item, filters);
                return null;
            })
        } else {
            this.updateFilter(e, filters);
        }

        this.tableSettingsStore.data = {
            ...this.tableSettingsStore.data,
            controlsEnabled: true
        };

         this.setState({
            pendingFilters: filters,
            hasPendingFilters: true,
            canSaveFilters: true
        });
    }

     applyFilters = () => {
        const updated = this.disabledFilterRules(this.state.pendingFilters);

        this.updateFilters(updated);

        this.setState({
            filters: updated,
            pendingFilters: updated,
            currentPage: 1,
            hasPendingFilters: false,
            canSaveFilters: true
        });
    }

    onClearFilter = (e: ChangeEventShim) => {
        const filters = { ...this.state.pendingFilters };
        filters[e.target.name] = undefined;
        Object.keys(filters).forEach((key) => {
            if (!filters[key] || (Array.isArray(filters[key]) && filters[key].length === 0)) {
                delete filters[key];
            }
            return null;
        })

        this.tableSettingsStore.data = {
            ...this.tableSettingsStore.data,
            controlsEnabled: true
        };

        this.setState({
            pendingFilters: filters,
            hasPendingFilters: true
        });
    }

    onRemoveFromList = (e: ChangeEventShim) => {

        const
            filters = { ...this.state.pendingFilters },
            {target} = e;

        if (target.name in filters && (Array.isArray(filters[target.name]) || filters[target.name] instanceof Set)) {
            let updated = filters[target.name];
            if (Array.isArray(filters[target.name])) {
                updated = new Set(filters[target.name]);
                updated.delete(target.value);
            } else {
                updated.delete(target.value);
            }
            const arr = Array.from(updated);
            filters[target.name] = arr.length === 0 ? undefined : arr;
            Object.keys(filters).forEach((key) => {
                if (!filters[key] || (Array.isArray(filters[key]) && filters[key].length === 0)) {
                    delete filters[key];
                }
                return null;
            })

            this.tableSettingsStore.data = {
                ...this.tableSettingsStore.data,
                controlsEnabled: true
            };

            this.setState({
                pendingFilters: filters,
                hasPendingFilters: true
            });

        }
    }

    toggleFilters() {
        const nextShowFilters = !this.state.showFilters;

        this.tableSettingsStore.data = {
            ...this.tableSettingsStore.data,
            showFilters: nextShowFilters
        };

        // Update UI
        this.setState({
            showFilters: nextShowFilters
        });
    }

    set itemsPerPage(value) {
        this.bustCache()
        this.setState({itemsPerPage: value}, () => {
            const data = this.tableSettingsStore.data || {};
            data.itemsPerPage = value;
            this.tableSettingsStore.data = data;
            this.getData();
        });
    }

    get itemsPerPage() {
        return this.state.itemsPerPage || 100;
    }

    set currentPage(value) {
        this.bustCache()
        this.setState({currentPage: value}, () => {
            const data = this.tableSettingsStore.data || {};
            data.currentPage = value;
            this.tableSettingsStore.data = data;
            this.getData();
        })
    }

    get currentPage() {
        return this.state.currentPage || 1;
    }

    // get sortOrder() {
    //     console.log( "getSortOrder" );
    //     return this.state.sortOrder;
    // }
    //
    // set sortOrder(sortOrder: "ascend" | "descend") {
    //     console.log( "setSortOrder" );
    //     this.bustCache()
    //     this.setState({sortOrder}, () => {
    //         const data = this.tableSettingsStore.data || {};
    //         data.sortOrder = sortOrder;
    //         this.tableSettingsStore.data = data;
    //         this.getData();
    //     });
    // }



    get sortField() {
        return this.state.sortField || this.defaultSortField;
    }

    set sortField(sortField: string) {
        this.bustCache()
        this.setState({sortField}, () => {
            const data = this.tableSettingsStore.data || {};
            data.sortField = sortField;
            this.tableSettingsStore.data = data;
            this.getData();
        });
    }

    updateColumnWidth = (column:any) => {
        this.tableSettingsStore.set({
            ...this.tableSettingsStore.data,
            columnsWidth: [...this.tableSettingsStore.data.columnsWidth.map((c:any) => { return c.key === column.column.key ? {...c, width: column.width} : c}),]
        })

        this.setState({ columnsWidth: [...this.state.columnsWidth.map((c:any) => { return c.key === column.column.key ? {...c, width: column.width} : c}),]})
    }

    updateAllFilters = (updated: Filter[]) => {
        this.bustCache()
        const filters: Record<any, any> = {};
        updated.forEach((filter) => {
            filters[filter.name] = filter.value;
        })
        this.FilterComponents = updated;

        this.updateFilters(filters)
        this.getData();
    }

    /**
     * Hook for subclasses to reshape a row before it is written to the Excel
     * export, e.g. to replace object-valued fields with readable strings.
     */
    protected exportRow(row: any): any {
        return row;
    }

    public onExport() {

        let filteredColumns = this.columns
            .sort((a, b) => {
                // @ts-ignore
                return (
                    this.state.columnOrder.indexOf(a.id) - this.state.columnOrder.indexOf(b.id)
                );
            })
            .filter((col) => {
                // @ts-ignore
                return this.state.disabledColumns.indexOf(col.id) === -1;
            })
            .map((col) => {
                return col;
            });

        let filteredRows = this.rows.map((row) => {
            const baseRow = {
                ...row,
                // @ts-ignore
                helixId: row?.helixId ? row?.helixId : 'Not Set',
                sharingStatus: row?.sharingStatus === 2 ? 'Fully Shared' : row?.sharingStatus === 1 ? 'Partially Shared' : 'Not Shared',
                STT: row?.STT ? 'Yes' : 'No',
                ownerName: row?.ownerName ? row?.ownerName : 'Not Set',
                workflowTypeTitle: row?.workflowTypeTitle ? row?.workflowTypeTitle : 'Not Set',
                expedited: row?.expedited ? 'Yes' : 'No',
                noExtension: row?.noExtension ? 'Yes' : 'No',
                launched: row?.launched ? 'Yes' : 'No',
                onHold: row?.onHold ? 'Yes' : 'No',
                invoiceMonth: row?.invoiceMonth ? DateTime.fromISO(row?.invoiceMonth).startOf('month').setLocale('en-gb').toFormat('MM-yyyy') : 'Not Set',
                monthCompleted: row?.monthCompleted ? DateTime.fromISO(row?.monthCompleted).startOf('month').setLocale('en-gb').toFormat('MM-yyyy') : 'Not Set',
                salesOrder: row?.salesOrder ? row?.salesOrder : 'Not Set',
                invoiceName: row?.invoiceName ? row?.invoiceName : 'Not Set',
                notes: row?.notes ? row?.notes.text : 'Not Set',
                dueDate: row?.dueDate ? DateTime.fromISO(row?.dueDate).setLocale('en-gb').toLocaleString(DateTime.DATETIME_SHORT): 'Not Set',
                importedTimestamp: row?.importedTimestamp ? DateTime.fromISO(row?.importedTimestamp).setLocale('en-gb').toLocaleString(DateTime.DATETIME_SHORT): 'Not Set',
                lastUpdatedTimestamp: DateTime.fromISO(row?.lastUpdatedTimestamp).setLocale('en-gb').toLocaleString(DateTime.DATETIME_SHORT),
                latestTranslationDueTimestamp: row?.latestTranslationDueTimestamp ? DateTime.fromISO(row?.latestTranslationDueTimestamp).setLocale('en-gb').toLocaleString(DateTime.DATETIME_SHORT): 'Not Set',
                translationDueTimestamp: row?.translationDueTimestamp ? DateTime.fromISO(row?.translationDueTimestamp).setLocale('en-gb').toLocaleString(DateTime.DATETIME_SHORT): 'Not Set',
                POIssued: row?.POIssued ? 'Yes' : 'No',
                legallySensitive: row?.legallySensitive?.isSet ? 'Yes' : 'No',
                PODate: row?.PODate ? DateTime.fromISO(row?.PODate).setLocale('en-gb').toLocaleString(DateTime.DATETIME_SHORT) : 'Not Set',
            };

            // Fix for Olive Batches
            if (this.title === "Batches" ) {
                const states = row.summaryStats?.states,
                    statesValue = states ? `I: ${states?.importing}, P: ${states?.pending}, C: ${states?.complete}, E: ${states?.error}` : "Not Set"

                return {
                    ...baseRow,
                    creator: row.creatorDetails?.name,
                    totalScripts: row.summaryStats?.totalScripts,
                    totalSourceWordCount: row.summaryStats?.totalSourceWordCount,
                    totalSourceCharacterCount: row.summaryStats?.totalSourceCharacterCount,
                    totalRowsInScope: row.summaryStats?.totalRowsInScope,
                    totalMajorErrors: row.summaryStats?.totalMajorErrors,
                    totalMinorErrors: row.summaryStats?.totalMinorErrors,
                    totalOtherErrors: row.summaryStats?.totalOtherErrors,
                    numberOfLanguages: row.languageStats?.length,
                    stateSummary: statesValue,
                    percentageComplete: row.summaryStats?.percentComplete,
                }
            }

            return baseRow;
        }).map((row) => this.exportRow(row));

        this.setState({showExportModal: !this.state.showExportModal});

        const toExcel = new ExportJsonExcel({
            fileName: this.state.exportFilename,
            datas: [{
                sheetName: `${this.title}-exported`,
                sheetData: filteredRows,
                sheetFilter: filteredColumns.map((v: any) => v.dataIndex),
                sheetHeader: filteredColumns.map((v: any) => {
                    let header: string;
                    switch (v.dataIndex) {
                        case "STT":
                            header = "STT";
                            break;
                        case "sourceLanguageCode":
                            header = "Source Language";
                            break;
                        case "languageCount":
                            header = "Language Count";
                            break;
                        case "totalWeightedWordCount":
                            header = "Total Weighted Word Count";
                            break;
                        case "twwc":
                            header = "Total Weighted Word Count";
                            break;
                        case "totalWordCount":
                            header = "Total Word Count";
                            break;
                        case "wc":
                            header = "Total Word Count";
                            break;
                        case "targetLanguageCode":
                            header = "Target Language";
                            break;
                        case "weightedWordCount":
                            header = "Weighted Word Count";
                            break;
                        case "engineeringHours":
                            header = "Engineering Hours";
                            break;
                        case "lastUpdatedTimestamp":
                            header = "Last Updated Timestamp";
                            break;
                        case "lastImportedTimestamp":
                            header = "Last Imported Timestamp";
                            break;
                        case "creationTimestamp":
                            header = "Creation Timestamp";
                            break;
                        case "internalQuoteLink":
                            header = "Internal Quote Link";
                            break;
                        case "internalCost":
                            header = "Internal Cost";
                            break;
                        case "accountSuspended":
                            header = "Account Suspended";
                            break;
                        case "isEnrolledIn2Sv":
                            header = "Is 2SV Enrolled";
                            break;
                        case "legallySensitive":
                            header = "Legally Sensitive";
                            break;
                        case "isActive":
                            header = "Active";
                            break;
                        case "rwsDeliveryDate":
                            header = "RWS Delivery Date";
                            break;
                        case "clientQuoteLink":
                            header = "Client Quote Link";
                            break;
                        case "googleRequestedDate":
                            header = "Google Requested Date";
                            break;
                        case "estimatedDeliveryDate":
                            header = "Estimated Delivery Date";
                            break;
                        case "monthCompleted":
                            header = "Month Completed";
                            break;
                        case "costPerTestCase":
                            header = "Cost Per Test Case";
                            break;
                        case "totalCostValidation":
                            header = "Total Cost Validation";
                            break;
                        case "reviewServiceType":
                            header = "Review Service Type";
                            break;
                        default:
                            header = v.title;
                            break;
                    }

                    if (v.id === "totalMercuryCharge") {
                        header = "Mercury Charge"
                    }
                    return header;
                })
            }]
        }); //new
        toExcel.saveExcel()
        return null
    }

    public onSaveFilters = async (data: { title: string, filters: FilterComponent[] }) => {


        this.userContext.gpSocket.filterStorage.emit( "create", {
            key: this.props.id || this.title,
            filterData: this.filters,
            name: data.title
        }, ( filter:any ) => {
            const
                activeFiltersList = Object.keys(filter.filterData),
                {savedFilters} = this.state;

            savedFilters.push(filter);

            this.updateFilterComponents(activeFiltersList)

            this.tableSettingsStore.data = {
                ...this.tableSettingsStore.data,
                currentSavedFilter: filter,
                moreLessOptions: activeFiltersList,
                controlsEnabled: false
            };
            this.setState({savedFilters, currentSavedFilter: filter})
        })
    }

    public onChangeLoadedFilter = async (filter: any) => {
        let defaultControlsEnabled =  false;

        const {data: dbFilter} = await this.userContext.gpClient.get(`/google-gp-v1/filterStorage/${filter._id}`, {params: {key: this.props.id ? this.props.id : this.title}})

        if (typeof dbFilter.filterData === "string") {
            dbFilter.filterData = JSON.parse(dbFilter.filterData);
        }

        // compare the current filter with db filter, if are not the same controls should be enabled
        if (JSON.stringify(filter.filterData) !== (JSON.stringify(dbFilter.filterData))) {
            defaultControlsEnabled = true;
        }

        const updated = this.disabledFilterRules(dbFilter.filterData);

        const
            activeFiltersList = Object.keys(dbFilter.filterData);

        this.updateFilterComponents(activeFiltersList)

        this.filterStore.clear();
        this.filterStore.add(updated);
        this.filterStore.save();

        this.tableSettingsStore.data = {
            ...this.tableSettingsStore.data,
            currentSavedFilter: dbFilter,
            controlsEnabled: defaultControlsEnabled,
            moreLessOptions: activeFiltersList
        };
        this.bustCache();
        this.setState({filters: updated, pendingFilters: updated, currentSavedFilter: dbFilter, currentPage: 1, hasPendingFilters: false, loading: true}, async () => await this.getData() );
    }

    public onDeleteSavedFilter = async (filterId: string) => {
        const deleteFilter = await this.userContext.gpClient.delete(`/google-gp-v1/filterStorage/${filterId}`);

        if (deleteFilter.data.acknowledged && deleteFilter.data.deletedCount > 0) {
            const savedFiltersWithoutCurrent = this.state.savedFilters.filter((f) => f._id !== filterId);

            // Decide which filters should be active after delete:
            // use the initial list if available, otherwise fall back to all filters.
            const restoredMoreLess =
                this.initialMoreLessOptions && this.initialMoreLessOptions.length > 0
                    ? this.initialMoreLessOptions
                    : this.FilterComponents.map(f => f.name);

            // Update FilterComponents.active based on that list
            this.updateFilterComponents(restoredMoreLess);

            // Optionally: clean up current filters so we only keep ones that are still visible
            const updatedFilters: TableFilters = { ...this.state.filters };
            Object.keys(updatedFilters).forEach((key) => {
                if (!restoredMoreLess.includes(key)) {
                    delete updatedFilters[key];
                }
            });

            // Persist to FilterStorage + URL
            this.filterStore.clear();
            this.filterStore.add(updatedFilters);
            this.filterStore.save();
            this.setFilterParams({ filters: updatedFilters });

            // Persist table settings
            this.tableSettingsStore.data = {
                ...this.tableSettingsStore.data,
                currentSavedFilter: undefined,
                moreLessOptions: restoredMoreLess,
                controlsEnabled: false
            };

            this.bustCache();

            this.setState(
                {
                    savedFilters: savedFiltersWithoutCurrent,
                    currentSavedFilter: undefined,
                    filters: updatedFilters,
                    currentPage: 1
                },
                this.getData
            );
        } else {
            notification.open({
                message: 'Failed to delete filter',
                description:
                    'The filter was not deleted. Please try again.',
            });
        }
    }


    public onUpdateSavedFilter = async (filter: SavedFilter) => {
        const updateFilter = await this.userContext.gpClient.patch(`/google-gp-v1/filterStorage/${filter._id}`, {
            key: filter.key,
            filterData: filter.filterData,
            sharedWith: filter.sharedWith,
            name: filter.name
        });

        if (updateFilter.status === 200) {
            this.tableSettingsStore.data = {
                ...this.tableSettingsStore.data,
                currentSavedFilter: filter,
                controlsEnabled: false
            };
            this.setState({currentSavedFilter: filter})
        } else {
            notification.open({
                message: 'Failed to update filter',
                description:
                    'The filter was not updated. Please try again.',
            });
        }

    }

    public onResetSavedFilter = async (filterId: string) => {
        const {data: dbFilter} = await this.userContext.gpClient.get(`/google-gp-v1/filterStorage/${filterId}`, {params: {key: this.props.id ? this.props.id : this.title}})

        if (dbFilter) {

            if (typeof dbFilter.filterData === "string") {
                dbFilter.filterData = JSON.parse(dbFilter.filterData);
            }

            const updated = this.disabledFilterRules(dbFilter.filterData);
            this.filterStore.clear();
            this.filterStore.add(updated);
            this.filterStore.save();

            const
                activeFiltersList = Object.keys(dbFilter.filterData);

            this.updateFilterComponents(activeFiltersList)

            this.tableSettingsStore.data = {
                ...this.tableSettingsStore.data,
                currentSavedFilter: dbFilter,
                moreLessOptions: activeFiltersList,
                controlsEnabled: false
            };

            this.bustCache();

            const savedFiltersWithoutCurrent = this.state.savedFilters.filter((f) => f._id !== filterId)

            this.setState({ savedFilters: [...savedFiltersWithoutCurrent, dbFilter], filters: updated,  pendingFilters: updated, currentSavedFilter: dbFilter, currentPage: 1,  hasPendingFilters: false}, this.getData);
        } else {
            console.error('No filter was found.')
        }
    }

    public updateFilterComponents = (list: any) => {

        this.FilterComponents = this.FilterComponents.map((f)=> {
            if (list.includes(f.name)) {
                f.active = true;
            } else {
                f.active = false;
            }
            return f;
        })
    }

    public onChangeMoreLess = (list: any) => {

        this.tableSettingsStore.data = {
            ...this.tableSettingsStore.data,
            moreLessOptions: list
        };

    }

    public onApplyMoreLess = (list: any) => {

        this.updateFilterComponents(list)

        this.tableSettingsStore.set({
            ...this.tableSettingsStore.data,
            controlsEnabled: true
        })

        let updatedFilters = this.state.filters;

        // remove unchecked filters
        Object.keys(updatedFilters).forEach((item) => {
            if (!list.includes(item)) {
                delete updatedFilters[item]
            }
        })

        this.updateFilters(updatedFilters);
    }

    select = (e: R) => {

        const
            {selectedRows} = this.state,
            exists = selectedRows.filter(r => r.key === e.key);

        let
            selected = [];

        if (exists.length) {
            selected = selectedRows.filter(r => r.key !== e.key);
        } else {
            selected = selectedRows.concat([e]);
        }

        this.setState({selectedRows: selected});
    }

    selectAll = (all: boolean) => {
        if (all) {
            this.setState({
                selectedRows: this.rows.map((item) => {
                    return item
                })
            });
        } else {
            this.setState({selectedRows: []});
        }
    }

    get rows() {

        let
            rows = this.state.rowData;

        if (this.mapping) {
            rows = this.state.rowData.map((row) => {
                const translated: Record<keyof R, any> = {} as any;
                Object.keys(row).forEach((key: keyof R) => {
                    if (key in this.mapping!) {
                        translated[this.mapping![key]] = row[key];
                    } else {
                        translated[key] = row[key]
                    }
                    return null;
                });
                return translated;
            }) as R[];
        }

        return rows.map((row, i) => {

            return {
                key: row._id || i,
                ...row
            }
        });
    }

    public order() {
        const { sortOrder } = this.state;
        switch (sortOrder) {
            case "ascend":
                return "descend";
            case "descend":
            default:
                return "ascend";
        }
    }

    sort(
        pagination: TablePaginationConfig,
        filters: Record<string, Key[] | null>,
        sorter: SorterResult<R> | SorterResult<R>[],
        extra: TableCurrentDataSource<R>
    ) {
        const {order, field} = sorter as SorterResult<R>;

        this.bustCache();
        this.setState({
            sortOrder: order || (this.state.sortOrder === "descend" ? "ascend" : "descend"),
            sortField: order !== undefined ? field : this.defaultSortField as any
        }, () => {

            const data = this.tableSettingsStore.data || {};
            data.sortOrder = this.state.sortOrder;
            data.sortField = this.state.sortOrder !== undefined ? field : this.defaultSortField
            this.tableSettingsStore.data = data;
            this.getData();
        })
    }

    sortById = (a: any, b: any) => {
        // @ts-ignore
        return this.state.columnOrder.indexOf(a.id) - this.state.columnOrder.indexOf(b.id)
    }

    renderActions(): any {
        return null;
    }

    public additionalExportOptionButtons: React.FunctionComponent<any>[] = []

    private renderAdditionalExportOptions() {
        return this.additionalExportOptionButtons.map((c, i) => {
            return c({
                key: i,
                state: this.state,
                context: this.props.context
            })
        });
    }

    public renderExportButton() {

        const { showExportModal, exportFilename } = this.state;

        return (
            <>
                {this.renderAdditionalExportOptions()}
                <Button
                    type="default"
                    icon={<DownloadOutlined/>}
                    className="export-btn"
                    onClick={() => this.setState({showExportModal: !showExportModal})}
                    disabled={this.state.loading}
                >
                    Export
                </Button>
                <Modal
                    title={"Export screen to excel"}
                    onCancel={() => this.setState({showExportModal: !showExportModal})}
                    open={showExportModal}
                    // okText={"Save"}
                    // okButtonProps={{
                    //     icon: <DownloadOutlined />,
                    //     disabled: exportFilename.length === 0
                    // }}
                    footer={
                        <>
                            <Button
                                type="default"
                                className="export-btn"
                                onClick={() => this.setState({showExportModal: !showExportModal})}
                            >
                                Cancel
                            </Button>
                            <Button
                                type="primary"
                                disabled={exportFilename.length === 0}
                                icon={<DownloadOutlined/>}
                                className="export-btn"
                                onClick={() => this.onExport()}
                            >
                                Save
                            </Button>

                        </>
                    }
                >
                    <Item>
                        <Input onChange={(e) => this.setState({exportFilename: e.target.value})}
                            value={exportFilename} type={"text"} placeholder={"Exported file name"}
                            ref={this.inputRef}
                               addonAfter=".xlsx"/>
                    </Item>
                </Modal>
            </>
        )
    }

    renderPagination() {

        let { currentPage, itemsPerPage } = this.state;

        if ( this.state.meta.total === 0 || !this.state.initialised || !itemsPerPage || !currentPage ) {
            return null;
        }

        let pageOptions = [
            "50",
            "100",
            "200",
            "500",
            "1000",
            "5000",
            "10000",
        ]


        return <Pagination
            defaultCurrent={currentPage}
            current={currentPage}
            total={this.state.meta.total}
            pageSize={itemsPerPage}
            onChange={ ( page, pageSize ) => {
                let
                    { currentPage, itemsPerPage } = this.state,
                    update = false;

                if ( page !== currentPage ) {
                    update = true;
                    currentPage = page;
                }
                if ( pageSize !== itemsPerPage ) {
                    update = true;
                    itemsPerPage = pageSize || 1;
                }
                if ( update ) {
                    this.bustCache()
                    const data = this.tableSettingsStore.data || {};
                    data.currentPage = currentPage;
                    data.itemsPerPage = itemsPerPage;
                    this.tableSettingsStore.data = data;
                    window.scrollTo(0, 0)
                    this.setState( { currentPage, itemsPerPage }, this.getData );
                }
            }}
            responsive={true}
            showTotal={(total, range) => total ? `${total} ${total > 1 ? 'items' : 'item'}` : '' }
            disabled={this.state.loading}
            // @ts-ignore
            simple={!this.state.breaks.xxl}
            pageSizeOptions={pageOptions}
        />
    }

    renderTitle() {


        return (
            <div className={"table-title-container"}>
                <div className={"table-title-left"}>
                    <Helmet>
                        <title>{this.pageTitle}</title>
                    </Helmet>
                    <Title
                        level={3}
                        className={"table-title"}
                    >
                        {this.title}

                    </Title>
                    {
                        this.props.displayHideFilters
                        && <div className={"filter-options-container"}>
                            <Tooltip
                                placement="bottom"
                                color={"var(--tertiary-color)"}
                                title={this.state.showFilters ? "Hide filters" : "View all filters"}
                            >
                                <Button
                                    type="primary"
                                    onClick={() => this.toggleFilters()}
                                    icon={
                                        !this.state.showFilters ? (
                                        <AlignLeftOutlined/>
                                        ) : (
                                        <CloseCircleOutlined/>
                                        )
                                    }
                                >
                                    Filters
                                </Button>
                            </Tooltip>
                        </div>


                    }

                </div>
            </div>
        )
    }

    filtersRef = createRef<any>();

    renderFilters() {

        const { hideDefaultFilters } = this.props;
        return (

            <>           
                <div className={"filterWrapperRef"} ref={this.filtersRef}>
                    <FiltersBar
                        title={this.title}
                        showFilters={this.state.showFilters}
                        hideFilters={hideDefaultFilters}
                        onToggleFilters={() => this.toggleFilters()}
                        onChange={hideDefaultFilters ? () => { } : this.updateAllFilters}
                        canSaveFilters={this.state.canSaveFilters}
                        filtersApplied={!this.state.hasPendingFilters && this.getAppliedFilterCount() > 0}
                        filters={hideDefaultFilters ? [] : this.FilterComponents.map((filter) => {
                            return {
                                id: filter.id,
                                component: filter.component,
                                title: filter.title,
                                key: filter.name,
                                name: filter.name,
                                value: Array.isArray(filter.name)
                                    ? filter.name.map(
                                        name => this.state.pendingFilters[name]
                                    )
                                    : this.state.pendingFilters[filter.name],
                                onRemove: this.onRemoveFromList,
                                onChange: this.onUpdateFilter,
                                disabled: false,
                                onClearFilter: this.onClearFilter,
                                active: filter.active
                            };
                        }) as any}
                        onSave={!hideDefaultFilters ? async (data) => {
                                        await this.onSaveFilters!(data as any);
                                        this.setState({canSaveFilters: false});
                                    } : undefined
                                }
                        onLoadedFilterChange={!hideDefaultFilters ? (filter) => {
                            this.onChangeLoadedFilter(filter)
                        } : undefined}
                        onLoadedFilterDelete={!hideDefaultFilters ? (filter) => {
                            this.onDeleteSavedFilter(filter)
                        } : undefined}
                        onLoadedFilterUpdate={!hideDefaultFilters ? (filter) => {
                            this.onUpdateSavedFilter(filter)
                        } : undefined}
                        onLoadedFilterReset={!hideDefaultFilters ? (filterId) => {
                            this.onResetSavedFilter(filterId)
                        } : undefined}
                        savedFilters={!hideDefaultFilters ? this.state.savedFilters : []}
                        filtersLoading={!hideDefaultFilters ? this.state.filtersLoading : false}
                        currentSavedFilter={!hideDefaultFilters ? this.state.currentSavedFilter : undefined}
                        appliedFilters={!hideDefaultFilters ? this.state.pendingFilters as any : []}
                        onClearAll={hideDefaultFilters ? () => { } : (params: any) => {
                            this.clear();
                        }}
                        loading={this.state.loading}
                        additionalButtons={!hideDefaultFilters ? [
                            <Button
                                size={"small"}
                                disabled={this.state.loading}
                                key={"reload"}
                                onClick={async () => {
                                    this.bustCache();
                                    await this.getData()
                                }}
                            >
                                        <ReloadOutlined spin={this.state.loading}/> Reload
                            </Button>,
                            <Button
                                size="small"
                                type="primary"
                                key="apply-filters"
                                icon={<FilterOutlined />}
                                disabled={ this.state.loading || (!this.state.hasPendingFilters && this.getAppliedFilterCount() === 0) }
                                onClick={this.applyFilters}
                            >
                                {
                                    this.state.hasPendingFilters
                                        ? "Apply Filters"
                                        : this.getAppliedFilterCount() > 0
                                            ? `Filters Applied (${this.getAppliedFilterCount()})`
                                            : "Apply Filters"
                                }
                            </Button>,
                            <Tooltip
                                key={"filters"}
                                title={this.state.showFilters ? "Hide filters" : "View all filters"}                                    
                                color={"var(--tertiary-color)"}
                                >
                                <Button
                                    size={"small"}
                                    type="primary"
                                    onClick={() => this.toggleFilters()}
                                    icon={
                                        !this.state.showFilters ? (
                                            <AlignLeftOutlined />
                                        ) : (
                                            <CloseCircleOutlined />
                                        )
                                    }
                                >
                                    Filters
                                </Button>   
                            </Tooltip> 
                                                    
                         ] : []}
                        controlsEnabled={hideDefaultFilters ? false : this.tableSettingsStore.data.controlsEnabled || false}
                        onApplyMoreLess={!hideDefaultFilters ? (list) => this.onApplyMoreLess(list) : undefined}
                        onChangeMoreLess={!hideDefaultFilters ? (list) => this.onChangeMoreLess(list) : undefined}
                    />
                </div>
            
            </>
        );
    }

    handleSubmitManageColumns = (data:any) => {
        const leftColumn = data.filter((item: any) => item.fixed === 'left').map((i: any) => i.id)
        const rightColumn = data.filter((item: any) => item.fixed === 'right').map((i: any) => i.id)
        const disabledColumns = data.filter((item:any) => item.active === false).map((i:any) => i.id)

        this.setState({
            columnOrder: data.map((i: any) => i.id),
            pinnedColumns: data.map((fixed: any) => fixed),
            pinnedLeft: leftColumn,
            pinnedRight: rightColumn,
            disabledColumns: disabledColumns
        })
    }

    renderManageColumns() {
        let columnFixed = this.columns.sort(this.sortById).map((col) => {
            let leftCol: any = this.state.pinnedLeft;
            let rightCol: any = this.state.pinnedRight;

            let pinnedLeft: any = leftCol.some((l: any) => col.id === l);
            let pinnedRight: any = rightCol.some((r: any) => col.id === r);

            return {
                ...col,
                // @ts-ignore
                fixed: pinnedLeft ? "left" : pinnedRight ? "right" : undefined,
            };
        });

        this.columns = columnFixed;
        const colsWithActive = this.columns.map((i: any) => {
            if (this.state.disabledColumns.includes(i.id)) {
                i = { ...i, active: false };
            } else {
                i = { ...i, active: true };
            }
            return i;
        });

        return (
            <ManageColumns
                columns={colsWithActive}
                disabledColumns={this.state.disabledColumns}
                isModalOpen={this.state.showManageColumnsModal}
                toggleModal={() => this.setState({showManageColumnsModal: !this.state.showManageColumnsModal})}
                handleSubmit={this.handleSubmitManageColumns}
            />
        )
    }

    renderManageTableHeight() {
        return <Tooltip
            placement="topLeft"
            color={"var(--tertiary-color)"}
            title={this.state.showFilters ? "Hide filters" : "Show filters"}
        >
            <Button
                type="text"
                icon={this.state.showFilters ? <FullscreenOutlined /> : <FullscreenExitOutlined style={{color:'var(--red)'}}/>}
                onClick={() => this.toggleFilters()}
                className="screen-size-btn"
            />
        </Tooltip>
    }

    renderConfigButtons() {
        return <div className="settings">
            {this.renderExportButton()}
            {this.renderManageColumns()}
            <Button className='back-top-btn' type={"text"} onClick={() => {
                window.scrollTo(0,0)
                if ( this.tableRef && this.tableRef.current ) {
                    this.tableRef.current.tableRef?.current?.scrollToTop(0)
                }
            }}><UpOutlined /></Button>
            {/*{this.renderManageTableHeight()}*/}
        </div>
    }

    /** Per-row class names: the `rowClassName` prop by default; subclasses may override. */
    public rowClassName(record: R, index: number): string {
        return this.props.rowClassName?.(record, index) ?? "";
    }

    tableRef:any = createRef();

    private handleSessionRestored = () => {
        this.reload();
    };

    isToolBarDisabled = (): boolean => {
        const {
            loading,
            initialised,
            filtersLoading,
            error,
            rowData,
            itemsPerPage,
            meta
        } = this.state;

        const rows: any[] = this.rows ?? rowData;
        const rowsLoaded = Array.isArray(rows) ? rows.length : 0;

        return !isSearchComplete({
            loading,
            initialised,
            filtersLoading,
            error,
            rowsLoaded,
            itemsPerPage,
            meta,
        });
    }


    renderTable(actions?: any) {

        if (this.state.error) {
            return (<ErrorWarning resetFilters={this.clear} reload={this.reload} error={this.state.error.toString()}/>);
        }

        const { rowData } = this.state;
        const cols = this.columnMapping().map((item) =>this.column(item as abstractColumn<R>));

        const disabled = this.isToolBarDisabled();
        const allowSelection = (this.props.noSelect ? !this.props.noSelect : !!actions) && !disabled;

        return (
            <BaseTable
                ref={this.tableRef}
                className='new-projects-table'
                dataSource={rowData}
                columns={ cols }
                onColumnSort={(sorter:any) => this.sort({}, {}, sorter, {} as any)}
                sortBy={{key: this.state.sortField ?? "", order:
                this.state.sortOrder === 'ascend'? 'asc' :
                this.state.sortOrder === 'descend' ? 'desc' 
                : this.state.sortOrder}}
                loading={this.state.loading}
                rowClassName={(record: R, index: number) => this.rowClassName(record, index)}
                rowSelection={
                    allowSelection
                        ? {
                            selectedRowKeys: this.state.selectedRows.map(
                                (r) => r.key
                            ) as Key[],
                            onSelectAll: this.selectAll,
                            onSelect: this.select,
                        }
                        : undefined
                }
                rowHeight={this.props.rowHeight}
                enabledRowSelection={typeof this.props.noSelect === "boolean" ? !this.props.noSelect : true}
                itemsPerPage={this.state.itemsPerPage}
                showFilters={this.state.showFilters}
                updateColumnWidth={this.updateColumnWidth}
                CellRenderer={this.props.CellRender}
                hasSidebar={this.props.hasSidebar}
                filtersRef={this.filtersRef}
                emptyRenderer={<EmptyOverlay reset={this.clear} loading={this.state.loading} />}
                useEstimatedRowHeight={this.props.useEstimatedRowHeight}
            />
        );}


    reload = async () => {
        this.bustCache()
        this.setState({error: undefined})
        await this.getData();
    }

    
    render() {
        const { hasSidebar } = this.props;
        
        if (!this.state.initialised) {
            return (
                <div className={"abstract-table ".concat(this.pageClass || "")}>
                    <LoadingStar logo={true} title={"Initialising..."} />
                </div>
            );
        }

        const actions = this.renderActions();
        // @ts-ignore
        const actionsClass = `${!this.state.breaks.xl ? 'action-btns smaller-actions' : 'action-btns'}`;
        const disabled = this.isToolBarDisabled();

        return (
            <div className={"abstract-table ".concat(this.pageClass || "")}>
                {this.renderFilters()}
                <div
                    className={`actions-bar-container ${hasSidebar ? "with-sidebar" : ""} ${disabled ? "is-disabled" : ""}`}
                    aria-disabled={disabled}
                >
                    {disabled ? (
                        <div className="loading-results">
                            <Spin spinning={true} size="small" indicator={<LoadingOutlined style={{ fontSize: 16, color: "#fff" }} spin />} />
                            <span>
                                Retrieving results...
                            </span>
                        </div>

                    ) : (
                        <>
                            <div className={actionsClass}>
                                {actions}
                            </div>
                            <div className="right-side">
                                {this.renderPagination()}
                                <span className="divider" />
                                {this.renderConfigButtons()}
                            </div>
                        </>
                    )}
                </div>

                <div className="table-wrapper-container">
                    {this.renderTable(actions)}
                </div>
            </div>
        );
    }


    public expandable(): ExpandableConfig<R> | undefined {
        return;
    }
}
